import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Check, X } from 'lucide-react';

// W-9 Form Component
export const W9Form = ({ onSubmit, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    name: initialData.name || '',
    business_name: initialData.business_name || '',
    tax_classification: initialData.tax_classification || 'individual',
    address: initialData.address || '',
    city_state_zip: initialData.city_state_zip || '',
    ssn: initialData.ssn || '',
    ein: initialData.ein || '',
    exempt_payee_code: initialData.exempt_payee_code || '',
    exemption_fatca_code: initialData.exemption_fatca_code || '',
    certification_agreed: false,
    signature_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.certification_agreed) {
      alert('Please certify the information is correct');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="bg-[var(--background-subtle)] p-4 rounded-lg mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Form W-9: Request for Taxpayer Identification Number
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Give Form W-9 to the requester. Do not send to the IRS.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Line 1 - Name */}
        <div>
          <Label className="font-semibold">1. Name (as shown on your income tax return)</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            placeholder="Enter your legal name"
            required
            className="mt-1"
          />
        </div>

        {/* Line 2 - Business Name */}
        <div>
          <Label className="font-semibold">2. Business name/disregarded entity name (if different from above)</Label>
          <Input
            value={formData.business_name}
            onChange={(e) => setFormData({...formData, business_name: e.target.value})}
            placeholder="Enter business name (if applicable)"
            className="mt-1"
          />
        </div>

        {/* Line 3 - Tax Classification */}
        <div>
          <Label className="font-semibold">3. Federal tax classification</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[
              { value: 'individual', label: 'Individual/Sole Proprietor' },
              { value: 'c_corp', label: 'C Corporation' },
              { value: 's_corp', label: 'S Corporation' },
              { value: 'partnership', label: 'Partnership' },
              { value: 'trust', label: 'Trust/Estate' },
              { value: 'llc', label: 'LLC' },
            ].map(opt => (
              <label key={opt.value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="tax_classification"
                  value={opt.value}
                  checked={formData.tax_classification === opt.value}
                  onChange={(e) => setFormData({...formData, tax_classification: e.target.value})}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {/* Line 4 - Exemptions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="font-semibold">4. Exempt payee code (if any)</Label>
            <Input
              value={formData.exempt_payee_code}
              onChange={(e) => setFormData({...formData, exempt_payee_code: e.target.value})}
              placeholder="Code"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">Exemption from FATCA code (if any)</Label>
            <Input
              value={formData.exemption_fatca_code}
              onChange={(e) => setFormData({...formData, exemption_fatca_code: e.target.value})}
              placeholder="Code"
              className="mt-1"
            />
          </div>
        </div>

        {/* Line 5 & 6 - Address */}
        <div>
          <Label className="font-semibold">5. Address (number, street, apt/suite)</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="Enter street address"
            required
            className="mt-1"
          />
        </div>

        <div>
          <Label className="font-semibold">6. City, State, and ZIP code</Label>
          <Input
            value={formData.city_state_zip}
            onChange={(e) => setFormData({...formData, city_state_zip: e.target.value})}
            placeholder="City, State ZIP"
            required
            className="mt-1"
          />
        </div>

        {/* Part I - TIN */}
        <div className="bg-[#D4AF37]/10 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">Part I: Taxpayer Identification Number (TIN)</h4>
          <p className="text-sm text-[var(--text-secondary)] mb-3">Enter your TIN in the appropriate box. For individuals, this is generally your social security number (SSN).</p>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Social Security Number (SSN)</Label>
              <Input
                value={formData.ssn}
                onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                placeholder="XXX-XX-XXXX"
                className="mt-1"
                maxLength={11}
              />
            </div>
            <div>
              <Label>Employer Identification Number (EIN)</Label>
              <Input
                value={formData.ein}
                onChange={(e) => setFormData({...formData, ein: e.target.value})}
                placeholder="XX-XXXXXXX"
                className="mt-1"
                maxLength={10}
              />
            </div>
          </div>
        </div>

        {/* Part II - Certification */}
        <div className="bg-[var(--warning)]/10 p-4 rounded-lg border border-[var(--warning)]/30">
          <h4 className="font-semibold mb-3">Part II: Certification</h4>
          <p className="text-sm mb-3">Under penalties of perjury, I certify that:</p>
          <ul className="text-sm list-disc list-inside space-y-1 mb-4">
            <li>The number shown on this form is my correct taxpayer identification number</li>
            <li>I am not subject to backup withholding</li>
            <li>I am a U.S. citizen or other U.S. person</li>
            <li>The FATCA code(s) entered (if any) are correct</li>
          </ul>
          
          <label className="flex items-start gap-3">
            <Checkbox
              checked={formData.certification_agreed}
              onCheckedChange={(checked) => setFormData({...formData, certification_agreed: checked})}
              className="mt-1"
            />
            <span className="text-sm font-medium">
              I certify that all information provided is true and correct to the best of my knowledge.
            </span>
          </label>
        </div>

        {/* Signature */}
        <div>
          <Label className="font-semibold">Date</Label>
          <Input
            type="date"
            value={formData.signature_date}
            onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
            required
            className="mt-1"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" className="flex-1 bg-[var(--primary)] text-white">
            <Check className="w-4 h-4 mr-2" />
            Submit W-9
          </Button>
        </div>
      </form>
    </div>
  );
};

// Direct Deposit Form Component
export const DirectDepositForm = ({ onSubmit, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    account_holder_name: initialData.account_holder_name || '',
    bank_name: initialData.bank_name || '',
    account_type: initialData.account_type || 'checking',
    routing_number: initialData.routing_number || '',
    account_number: initialData.account_number || '',
    confirm_account_number: '',
    authorization_agreed: false,
    signature_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.account_number !== formData.confirm_account_number) {
      alert('Account numbers do not match');
      return;
    }
    if (!formData.authorization_agreed) {
      alert('Please authorize direct deposit');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="bg-[var(--success)]/15 p-4 rounded-lg mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Direct Deposit Authorization Form
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Complete this form to receive your payments via direct deposit.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Account Holder Info */}
        <div>
          <Label className="font-semibold">Account Holder Name (as it appears on bank account)</Label>
          <Input
            value={formData.account_holder_name}
            onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
            placeholder="Enter name on bank account"
            required
            className="mt-1"
          />
        </div>

        <div>
          <Label className="font-semibold">Bank/Financial Institution Name</Label>
          <Input
            value={formData.bank_name}
            onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
            placeholder="Enter bank name"
            required
            className="mt-1"
          />
        </div>

        {/* Account Type */}
        <div>
          <Label className="font-semibold">Account Type</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="account_type"
                value="checking"
                checked={formData.account_type === 'checking'}
                onChange={(e) => setFormData({...formData, account_type: e.target.value})}
              />
              Checking
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="account_type"
                value="savings"
                checked={formData.account_type === 'savings'}
                onChange={(e) => setFormData({...formData, account_type: e.target.value})}
              />
              Savings
            </label>
          </div>
        </div>

        {/* Bank Account Details */}
        <div className="bg-[#D4AF37]/10 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">Bank Account Information</h4>
          <p className="text-sm text-[var(--text-secondary)] mb-3">
            You can find your routing and account numbers at the bottom of your checks or in your online banking.
          </p>
          
          <div className="space-y-4">
            <div>
              <Label>Routing Number (9 digits)</Label>
              <Input
                value={formData.routing_number}
                onChange={(e) => setFormData({...formData, routing_number: e.target.value.replace(/\D/g, '').slice(0, 9)})}
                placeholder="Enter 9-digit routing number"
                required
                className="mt-1"
                maxLength={9}
              />
            </div>
            
            <div>
              <Label>Account Number</Label>
              <Input
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value.replace(/\D/g, '')})}
                placeholder="Enter account number"
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Confirm Account Number</Label>
              <Input
                value={formData.confirm_account_number}
                onChange={(e) => setFormData({...formData, confirm_account_number: e.target.value.replace(/\D/g, '')})}
                placeholder="Re-enter account number"
                required
                className="mt-1"
              />
              {formData.confirm_account_number && formData.account_number !== formData.confirm_account_number && (
                <p className="text-[var(--error)] text-sm mt-1">Account numbers do not match</p>
              )}
            </div>
          </div>
        </div>

        {/* Authorization */}
        <div className="bg-[var(--warning)]/10 p-4 rounded-lg border border-[var(--warning)]/30">
          <h4 className="font-semibold mb-3">Authorization</h4>
          <p className="text-sm mb-4">
            I hereby authorize NC Help Hub to initiate credit entries to my account at the financial institution named above. 
            I also authorize the financial institution to credit my account. This authority will remain in effect until I 
            provide written notification to cancel it.
          </p>
          
          <label className="flex items-start gap-3">
            <Checkbox
              checked={formData.authorization_agreed}
              onCheckedChange={(checked) => setFormData({...formData, authorization_agreed: checked})}
              className="mt-1"
            />
            <span className="text-sm font-medium">
              I authorize direct deposit to the account specified above.
            </span>
          </label>
        </div>

        {/* Date */}
        <div>
          <Label className="font-semibold">Date</Label>
          <Input
            type="date"
            value={formData.signature_date}
            onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
            required
            className="mt-1"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" className="flex-1 bg-green-500 hover:bg-[var(--success)] text-white">
            <Check className="w-4 h-4 mr-2" />
            Submit Direct Deposit
          </Button>
        </div>
      </form>
    </div>
  );
};

// I-9 Form Component (Section 1 - Employee)
export const I9Form = ({ onSubmit, onCancel, initialData = {} }) => {
  const [formData, setFormData] = useState({
    last_name: initialData.last_name || '',
    first_name: initialData.first_name || '',
    middle_initial: initialData.middle_initial || '',
    other_last_names: initialData.other_last_names || '',
    address: initialData.address || '',
    apt_number: initialData.apt_number || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip_code: initialData.zip_code || '',
    date_of_birth: initialData.date_of_birth || '',
    ssn: initialData.ssn || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    citizenship_status: initialData.citizenship_status || '',
    alien_number: initialData.alien_number || '',
    i94_number: initialData.i94_number || '',
    foreign_passport_number: initialData.foreign_passport_number || '',
    country_of_issuance: initialData.country_of_issuance || '',
    work_authorization_expiration: initialData.work_authorization_expiration || '',
    attestation_agreed: false,
    signature_date: new Date().toISOString().split('T')[0]
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.citizenship_status) {
      alert('Please select your citizenship/immigration status');
      return;
    }
    if (!formData.attestation_agreed) {
      alert('Please sign the attestation');
      return;
    }
    onSubmit(formData);
  };

  return (
    <div className="max-h-[80vh] overflow-y-auto">
      <div className="bg-[#F4C2C2]/15 p-4 rounded-lg mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Form I-9: Employment Eligibility Verification
        </h3>
        <p className="text-sm text-[var(--text-secondary)] mt-1">
          Section 1: Employee Information and Attestation (To be completed by employee)
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="font-semibold">Last Name *</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">First Name *</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">Middle Initial</Label>
            <Input
              value={formData.middle_initial}
              onChange={(e) => setFormData({...formData, middle_initial: e.target.value})}
              maxLength={1}
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label>Other Last Names Used (if any)</Label>
          <Input
            value={formData.other_last_names}
            onChange={(e) => setFormData({...formData, other_last_names: e.target.value})}
            placeholder="Maiden name, etc."
            className="mt-1"
          />
        </div>

        {/* Address */}
        <div className="grid grid-cols-4 gap-3">
          <div className="col-span-2">
            <Label className="font-semibold">Address *</Label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              placeholder="Street address"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label>Apt. Number</Label>
            <Input
              value={formData.apt_number}
              onChange={(e) => setFormData({...formData, apt_number: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">City *</Label>
            <Input
              value={formData.city}
              onChange={(e) => setFormData({...formData, city: e.target.value})}
              required
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-semibold">State *</Label>
            <Input
              value={formData.state}
              onChange={(e) => setFormData({...formData, state: e.target.value})}
              placeholder="State"
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">ZIP Code *</Label>
            <Input
              value={formData.zip_code}
              onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
              required
              className="mt-1"
            />
          </div>
        </div>

        {/* Personal Info */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="font-semibold">Date of Birth *</Label>
            <Input
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label className="font-semibold">Social Security Number *</Label>
            <Input
              value={formData.ssn}
              onChange={(e) => setFormData({...formData, ssn: e.target.value})}
              placeholder="XXX-XX-XXXX"
              required
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email Address</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="mt-1"
            />
          </div>
        </div>

        {/* Citizenship Status */}
        <div className="bg-[#D4AF37]/10 p-4 rounded-lg">
          <h4 className="font-semibold mb-3">Citizenship/Immigration Status *</h4>
          <p className="text-sm text-[var(--text-secondary)] mb-3">Check one of the following:</p>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="citizenship_status"
                value="citizen"
                checked={formData.citizenship_status === 'citizen'}
                onChange={(e) => setFormData({...formData, citizenship_status: e.target.value})}
                className="mt-1"
              />
              <span>A citizen of the United States</span>
            </label>
            
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="citizenship_status"
                value="noncitizen_national"
                checked={formData.citizenship_status === 'noncitizen_national'}
                onChange={(e) => setFormData({...formData, citizenship_status: e.target.value})}
                className="mt-1"
              />
              <span>A noncitizen national of the United States</span>
            </label>
            
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="citizenship_status"
                value="permanent_resident"
                checked={formData.citizenship_status === 'permanent_resident'}
                onChange={(e) => setFormData({...formData, citizenship_status: e.target.value})}
                className="mt-1"
              />
              <span>A lawful permanent resident (Alien Registration Number/USCIS Number)</span>
            </label>
            
            <label className="flex items-start gap-3">
              <input
                type="radio"
                name="citizenship_status"
                value="authorized_alien"
                checked={formData.citizenship_status === 'authorized_alien'}
                onChange={(e) => setFormData({...formData, citizenship_status: e.target.value})}
                className="mt-1"
              />
              <span>An alien authorized to work until (expiration date, if applicable)</span>
            </label>
          </div>
        </div>

        {/* Additional fields for non-citizens */}
        {(formData.citizenship_status === 'permanent_resident' || formData.citizenship_status === 'authorized_alien') && (
          <div className="bg-[var(--background-subtle)] p-4 rounded-lg space-y-3">
            <h4 className="font-semibold">Additional Information for Non-Citizens</h4>
            
            {formData.citizenship_status === 'permanent_resident' && (
              <div>
                <Label>Alien Registration Number/USCIS Number</Label>
                <Input
                  value={formData.alien_number}
                  onChange={(e) => setFormData({...formData, alien_number: e.target.value})}
                  className="mt-1"
                />
              </div>
            )}
            
            {formData.citizenship_status === 'authorized_alien' && (
              <>
                <div>
                  <Label>Work Authorization Expiration Date</Label>
                  <Input
                    type="date"
                    value={formData.work_authorization_expiration}
                    onChange={(e) => setFormData({...formData, work_authorization_expiration: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Alien Registration Number/USCIS Number (if applicable)</Label>
                    <Input
                      value={formData.alien_number}
                      onChange={(e) => setFormData({...formData, alien_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Form I-94 Admission Number (if applicable)</Label>
                    <Input
                      value={formData.i94_number}
                      onChange={(e) => setFormData({...formData, i94_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Foreign Passport Number (if applicable)</Label>
                    <Input
                      value={formData.foreign_passport_number}
                      onChange={(e) => setFormData({...formData, foreign_passport_number: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Country of Issuance (if applicable)</Label>
                    <Input
                      value={formData.country_of_issuance}
                      onChange={(e) => setFormData({...formData, country_of_issuance: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Attestation */}
        <div className="bg-[var(--warning)]/10 p-4 rounded-lg border border-[var(--warning)]/30">
          <h4 className="font-semibold mb-3">Employee Attestation</h4>
          <p className="text-sm mb-4">
            I attest, under penalty of perjury, that I am (check one of the above boxes), that the information 
            I have provided is true and correct, and that I am aware that providing false information may 
            subject me to criminal penalties.
          </p>
          
          <label className="flex items-start gap-3">
            <Checkbox
              checked={formData.attestation_agreed}
              onCheckedChange={(checked) => setFormData({...formData, attestation_agreed: checked})}
              className="mt-1"
            />
            <span className="text-sm font-medium">
              I attest that the information provided is true and correct.
            </span>
          </label>
        </div>

        {/* Signature Date */}
        <div>
          <Label className="font-semibold">Today's Date</Label>
          <Input
            type="date"
            value={formData.signature_date}
            onChange={(e) => setFormData({...formData, signature_date: e.target.value})}
            required
            className="mt-1"
          />
        </div>

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" className="flex-1 bg-[#F4C2C2]/100 hover:bg-purple-600 text-white">
            <Check className="w-4 h-4 mr-2" />
            Submit I-9
          </Button>
        </div>
      </form>
    </div>
  );
};

export default { W9Form, DirectDepositForm, I9Form };
